import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";
import CreateDesignEntryFlow from "../features/pages/Campaign/components/CreateDesignEntryFlow";
import UpgradeDrawer from "../components/saas/UpgradeDrawer";
import { useActiveAccountId } from "./account-branding-context";
import { useSaasEntitlements } from "../hooks/useSaasEntitlements";
import { resolveCreateDraftGate } from "../utils/saasGenerateGate";
import {
  hasCreateDesignIntent,
  stripCreateDesignIntent,
} from "../utils/saasCreateDesignQuery";

const CreateDesignContext = createContext(null);

/**
 * Layout-level Create Design host — opens the modal in place on any page.
 */
export function CreateDesignProvider({ children }) {
  const activeAccountId = useActiveAccountId();
  const [searchParams, setSearchParams] = useSearchParams();
  const saasEntitlementsState = useSaasEntitlements(activeAccountId);
  const saasEntitlements = useMemo(
    () => saasEntitlementsState?.entitlements || {},
    [saasEntitlementsState?.entitlements]
  );
  const entitlementsReady = saasEntitlementsState?.isLoading !== true;

  const [isOpen, setIsOpen] = useState(false);
  const [upgradeDrawer, setUpgradeDrawer] = useState({ open: false, code: null });
  const [pendingIntent, setPendingIntent] = useState(false);

  const consumeCreateDesignQuery = useCallback(() => {
    if (!hasCreateDesignIntent(searchParams)) return;
    setSearchParams(stripCreateDesignIntent(searchParams), { replace: true });
  }, [searchParams, setSearchParams]);

  const applyCreateIntent = useCallback(() => {
    const gate = resolveCreateDraftGate(saasEntitlements);
    if (!gate.allowed) {
      setIsOpen(false);
      setUpgradeDrawer({ open: true, code: gate.reason || "FREE_DRAFT_LIMIT" });
    } else {
      setUpgradeDrawer({ open: false, code: null });
      setIsOpen(true);
    }
    consumeCreateDesignQuery();
  }, [saasEntitlements, consumeCreateDesignQuery]);

  const openCreateDesign = useCallback(() => {
    if (!entitlementsReady) {
      setPendingIntent(true);
      return;
    }
    applyCreateIntent();
  }, [entitlementsReady, applyCreateIntent]);

  const closeCreateDesign = useCallback(() => {
    setIsOpen(false);
    consumeCreateDesignQuery();
  }, [consumeCreateDesignQuery]);

  useEffect(() => {
    if (!hasCreateDesignIntent(searchParams)) return;
    if (!entitlementsReady) {
      setPendingIntent(true);
      return;
    }
    applyCreateIntent();
  }, [searchParams, entitlementsReady, applyCreateIntent]);

  useEffect(() => {
    if (!pendingIntent || !entitlementsReady) return;
    setPendingIntent(false);
    applyCreateIntent();
  }, [pendingIntent, entitlementsReady, applyCreateIntent]);

  const value = useMemo(
    () => ({
      isOpen,
      openCreateDesign,
      closeCreateDesign,
    }),
    [isOpen, openCreateDesign, closeCreateDesign]
  );

  return (
    <CreateDesignContext.Provider value={value}>
      {children}
      <CreateDesignEntryFlow
        isOpen={isOpen}
        onCancel={closeCreateDesign}
        onPaywall={(code) => {
          closeCreateDesign();
          setUpgradeDrawer({ open: true, code: code || "FREE_DRAFT_LIMIT" });
        }}
      />
      <UpgradeDrawer
        open={upgradeDrawer.open}
        code={upgradeDrawer.code}
        accountId={activeAccountId}
        onClose={() => setUpgradeDrawer({ open: false, code: null })}
      />
    </CreateDesignContext.Provider>
  );
}

CreateDesignProvider.propTypes = {
  children: PropTypes.node,
};

export function useCreateDesign() {
  const ctx = useContext(CreateDesignContext);
  if (!ctx) {
    throw new Error("useCreateDesign must be used within CreateDesignProvider");
  }
  return ctx;
}
